import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiClient, Campaign, DiallerImportResult, DiallerList, DiallerRecord, PreviewLead, ScriptDetail, ScriptSummary, ScriptUpsert,
} from '@arcnet/contracts';
import { api } from './client.js';

/** Dialler lists (docs/36 D1b). The client's own: nobody above the account can read them (0123). */
const dk = (tenantId: string, ...rest: string[]) => ['dialler', tenantId, ...rest] as const;

export const useDiallerLists = (tenantId: string) =>
  useQuery({
    queryKey: dk(tenantId, 'lists'),
    queryFn: () => api.get<{ data: DiallerList[] }>(`/v1/tenants/${tenantId}/dialler/lists`),
    enabled: !!tenantId,
  });

export const useDiallerRecords = (
  tenantId: string, listId: string | null, f: { q?: string; state?: string; offset?: number },
) =>
  useQuery({
    queryKey: dk(tenantId, 'records', listId ?? '', f.q ?? '', f.state ?? '', String(f.offset ?? 0)),
    queryFn: () => {
      const p = new URLSearchParams({ limit: '100', offset: String(f.offset ?? 0) });
      if (f.q) p.set('q', f.q);
      if (f.state) p.set('state', f.state);
      return api.get<{ data: DiallerRecord[]; total: number }>(
        `/v1/tenants/${tenantId}/dialler/lists/${listId}/records?${p}`);
    },
    enabled: !!tenantId && !!listId,
    placeholderData: (prev) => prev,
  });

/** What feeds a list from outside (docs/38): API clients with the scope leads:write. */
export const useDiallerApiClients = (tenantId: string) =>
  useQuery({
    queryKey: dk(tenantId, 'api-clients'),
    queryFn: () => api.get<{ data: ApiClient[] }>(`/v1/tenants/${tenantId}/dialler/api-clients`),
    enabled: !!tenantId,
  });

export const useDiallerDnc = (tenantId: string, q: string) =>
  useQuery({
    queryKey: dk(tenantId, 'dnc', q),
    queryFn: () => api.get<{ data: Array<{ phone: string; reason: string | null; created_at: string }>; total: number }>(
      `/v1/tenants/${tenantId}/dialler/dnc${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    enabled: !!tenantId,
    placeholderData: (prev) => prev,
  });

function useDiallerMutation<V, R = unknown>(tenantId: string, fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dialler', tenantId] }); },
  });
}

export const useSaveDiallerList = (tenantId: string) =>
  useDiallerMutation(tenantId, ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
    id ? api.put<{ id: string }>(`/v1/tenants/${tenantId}/dialler/lists/${id}`, body)
      : api.post<{ id: string }>(`/v1/tenants/${tenantId}/dialler/lists`, body));

export const useDeleteDiallerList = (tenantId: string) =>
  useDiallerMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/dialler/lists/${id}`));

export const useDeleteDiallerRecord = (tenantId: string) =>
  useDiallerMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/dialler/records/${id}`));

/** One slice of a spreadsheet. Not invalidating on every slice: the page refreshes once, at the end. */
export const useImportSlice = (tenantId: string, listId: string) =>
  useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<DiallerImportResult>(`/v1/tenants/${tenantId}/dialler/lists/${listId}/import`, body),
  });

/** The secret comes back this once. */
export const useCreateApiClient = (tenantId: string) =>
  useDiallerMutation(tenantId, (body: { name: string; list_id: string }) =>
    api.post<ApiClient & { secret: string }>(`/v1/tenants/${tenantId}/dialler/api-clients`, body));

export const useUpdateApiClient = (tenantId: string) =>
  useDiallerMutation(tenantId, ({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.patch(`/v1/tenants/${tenantId}/dialler/api-clients/${id}`, body));

export const useRevokeApiClient = (tenantId: string) =>
  useDiallerMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/dialler/api-clients/${id}`));

export const useAddDnc = (tenantId: string) =>
  useDiallerMutation(tenantId, (body: { phones: string[]; reason: string | null }) =>
    api.post<{ added: number; already: number; not_numbers: number; suppressed_on_lists: number }>(
      `/v1/tenants/${tenantId}/dialler/dnc`, body));

export const useRemoveDnc = (tenantId: string) =>
  useDiallerMutation(tenantId, (phone: string) => api.delete(`/v1/tenants/${tenantId}/dialler/dnc/${phone}`));

// ======================================================================= campaigns (0127)

export type CampaignDetail = Campaign & { missing: string[] };

export const useCampaigns = (tenantId: string) =>
  useQuery({
    queryKey: dk(tenantId, 'campaigns'),
    queryFn: () => api.get<{ data: Campaign[] }>(`/v1/tenants/${tenantId}/campaigns`),
    enabled: !!tenantId,
    refetchInterval: 30_000,
  });

export const useCampaign = (tenantId: string, id: string | null) =>
  useQuery({
    queryKey: dk(tenantId, 'campaign', id ?? ''),
    queryFn: () => api.get<CampaignDetail>(`/v1/tenants/${tenantId}/campaigns/${id}`),
    enabled: !!tenantId && !!id,
    refetchInterval: 15_000,
  });

export const useCampaignAttempts = (tenantId: string, id: string | null) =>
  useQuery({
    queryKey: dk(tenantId, 'campaign-attempts', id ?? ''),
    queryFn: () => api.get<{ data: Array<{
      id: string; phone: string; started_at: string; answered_at: string | null; ended_at: string | null;
      outcome: string | null; disposition: string | null; note: string | null; amd_result: string | null;
      amd_engine: string | null; amd_phrase: string | null; amd_charge: string | null;
      agent_number: string | null; agent_name: string | null; first_name: string | null; last_name: string | null;
    }> }>(`/v1/tenants/${tenantId}/campaigns/${id}/attempts`),
    enabled: !!tenantId && !!id,
    refetchInterval: 30_000,
  });

export const useSaveCampaign = (tenantId: string) =>
  useDiallerMutation(tenantId, ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
    id ? api.put<{ ok: true }>(`/v1/tenants/${tenantId}/campaigns/${id}`, body).then(() => ({ id }))
      : api.post<{ id: string }>(`/v1/tenants/${tenantId}/campaigns`, body));

export const useCampaignStatus = (tenantId: string) =>
  useDiallerMutation(tenantId, ({ id, status }: { id: string; status: 'running' | 'paused' | 'finished' }) =>
    api.post(`/v1/tenants/${tenantId}/campaigns/${id}/status`, { status }));

export const useDeleteCampaign = (tenantId: string) =>
  useDiallerMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/campaigns/${id}`));

// ---- call scripts (docs/36 §8.3, 0129)

export const useScripts = (tenantId: string, enabled = true) =>
  useQuery({
    queryKey: dk(tenantId, 'scripts'),
    queryFn: () => api.get<{ data: ScriptSummary[] }>(`/v1/tenants/${tenantId}/dialler-scripts`),
    enabled: !!tenantId && enabled,
  });

export const useScript = (tenantId: string, id: string | null) =>
  useQuery({
    queryKey: dk(tenantId, 'script', id ?? ''),
    queryFn: () => api.get<ScriptDetail>(`/v1/tenants/${tenantId}/dialler-scripts/${id}`),
    enabled: !!tenantId && !!id,
  });

export const useSaveScript = (tenantId: string) =>
  useDiallerMutation(tenantId, ({ id, ...body }: { id?: string } & ScriptUpsert) =>
    id ? api.put<{ ok: true; version: number }>(`/v1/tenants/${tenantId}/dialler-scripts/${id}`, body).then((r) => ({ id, version: r.version }))
      : api.post<{ id: string; version: number }>(`/v1/tenants/${tenantId}/dialler-scripts`, body));

export const useDeleteScript = (tenantId: string) =>
  useDiallerMutation(tenantId, (id: string) => api.delete(`/v1/tenants/${tenantId}/dialler-scripts/${id}`));

// ---- the agent's side (preview)

export interface MyCampaign { id: string; name: string; mode: string; due_now: number; callbacks_due: number; open_attempt_id: string | null }

export const useMyCampaigns = (tenantId: string, extensionId: string, enabled = true) =>
  useQuery({
    queryKey: dk(tenantId, 'my-campaigns', extensionId),
    queryFn: () => api.get<{ data: MyCampaign[]; tz: string }>(`/v1/tenants/${tenantId}/extensions/${extensionId}/campaigns`),
    enabled: !!tenantId && !!extensionId && enabled,
    refetchInterval: 20_000,
    retry: false,
  });

export const useNextLead = (tenantId: string, extensionId: string) =>
  useMutation({
    mutationFn: (campaignId: string) =>
      api.post<PreviewLead | { none: string }>(`/v1/tenants/${tenantId}/extensions/${extensionId}/campaigns/${campaignId}/next`, {}),
  });

export const useLeadResult = (tenantId: string, extensionId: string) =>
  useDiallerMutation(tenantId, ({ attemptId, ...body }: { attemptId: string } & Record<string, unknown>) =>
    api.post<{ ok: true; lead: string; next_at: string | null }>(
      `/v1/tenants/${tenantId}/extensions/${extensionId}/campaign-attempts/${attemptId}/result`, body));
